import {
  VmDemoScenario,
  VmPatient,
  VmSettings,
  VmSmsReason,
  VmTranscript,
  Voicemail,
} from '@models';
import {
  createDemoSettings,
  createDemoTranscripts,
  createDemoVoicemails,
  VM_DEMO_PATIENTS,
} from './voicemail-demo.data';
import { VoicemailGateway, VoicemailGatewayError } from './voicemail.gateway';

/** Fictional, in-memory data. This service never provisions a number or sends SMS. */
export class DemoVoicemailGateway extends VoicemailGateway {
  private scenario: VmDemoScenario = 'default';
  private revision = 0;
  private messages: Voicemail[];
  private readonly transcripts = createDemoTranscripts();
  private settings: VmSettings;
  private readonly providerName: () => string;

  constructor(providerName: () => string = () => 'Your practice') {
    super();
    this.providerName = providerName;
    this.messages = createDemoVoicemails(new Date(), this.providerName());
    this.settings = createDemoSettings(providerName());
  }

  override getTranscript(id: string): Promise<VmTranscript> {
    const message = this.messages.find(item => item.id === id);
    const status = message?.transcript.status ?? 'failed';

    return Promise.resolve({
      status,
      text: status === 'ready' ? (this.transcripts[id] ?? '') : '',
    });
  }

  override reset(): void {
    this.setScenario('default');
  }

  setScenario(scenario: VmDemoScenario | string): void {
    this.revision++;
    this.scenario = scenario as VmDemoScenario;
    this.messages = createDemoVoicemails(new Date(), this.providerName());
    this.settings = createDemoSettings(this.providerName());

    switch (scenario) {
      case 'empty':
        this.messages = [];
        break;
      case 'setup':
      case 'not-configured':
        this.messages = [];
        this.settings.state = 'not-configured';
        this.settings.phoneNumber = null;
        break;
      case 'processing':
        this.messages[0].transcript = { status: 'processing' };
        break;
      case 'provisioning-failed':
        this.settings.state = 'failed';
        this.settings.phoneNumber = null;
        break;
      case 'checking':
      case 'support-required':
      case 'provisioning':
        this.settings.state = scenario;
        this.settings.phoneNumber = null;
        break;
      case 'no-destination':
        // Scheduling type None and no override: nothing resolves, the prompt is not offered.
        this.settings.state = 'not-configured';
        this.settings.phoneNumber = null;
        this.settings.bookingLink = { url: null, source: null };
        this.messages = [];
        break;
      case 'unpublished-landing-page':
        // Scheduler chosen but the landing page is unpublished, so the link cannot be built.
        this.settings.bookingLink = { url: null, source: null };
        this.skipSms('unknown', 'no-destination');
        break;
      case 'override-link':
        this.settings.overrideUrl = 'https://calendly.com/alex-reed';
        break;
      case 'sms-unavailable-setup':
        this.settings.state = 'not-configured';
        this.settings.phoneNumber = null;
        this.messages = [];
        this.settings.smsAvailable = false;
        break;
      case 'sms-unavailable':
        this.settings.smsAvailable = false;
        this.messages.forEach(item => this.skipSms(item.id, 'sms-unavailable'));
        break;
      case 'appointment-booked':
        this.skipSms('jane', 'appointment-booked');
        break;
      case 'eligibility-unavailable':
        this.skipSms('unknown', 'eligibility-unavailable');
        break;
      case 'opted-out':
        this.skipSms('jane', 'opted-out');
        break;
      case 'delivery-blocked':
        this.skipSms('jane', 'delivery-blocked');
        break;
      case 'match-changed':
        this.skipSms('jane', 'match-changed');
        break;
      case 'no-response':
        this.skipSms('unknown', 'no-response');
        break;
      case 'not-requested':
        this.skipSms('unknown', 'not-requested');
        break;
      case 'input-unavailable':
        this.skipSms('unknown', 'input-unavailable');
        break;
      case 'unknown-outcome':
        this.messages[0].sms.status = 'unknown';
        this.messages[0].sms.sentAt = null;
        break;
      case 'withheld':
        this.messages.find(item => item.id === 'unknown')!.callerNumber = null;
        this.skipSms('unknown', 'no-number');
        break;
      case 'audio-error':
        this.messages[0].recordingRef = '/assets/voicemail/unavailable.mp3';
        break;
      case 'sms-sending':
        this.messages[0].sms.status = 'sending';
        this.messages[0].sms.sentAt = null;
        break;
    }
  }

  /**
   * The demo keeps real sample files in assets, so the handle is a path and this just fetches it.
   * The production gateway resolves the same handle through an authorized API request instead --
   * which is the whole reason the player asks the gateway rather than setting an <audio src>.
   */
  override async getAudio(recordingRef: string): Promise<Blob> {
    if (!recordingRef) {
      throw new Error('This voicemail has no recording.');
    }

    const response = await fetch(recordingRef);

    if (!response.ok) {
      throw new Error('The recording could not be loaded.');
    }

    return response.blob();
  }

  /**
   * The demo has no voice to speak with. A browser voice standing in would be exactly the wrong
   * preview -- the point of it is to hear the voice callers hear -- so the demo says so instead.
   */
  override async getPreviewAudio(): Promise<Blob> {
    await this.delay();
    throw new VoicemailGatewayError(
      'The call preview is not available in the demo.',
      'VOICEMAIL_PREVIEW_UNAVAILABLE'
    );
  }

  override async list(): Promise<Voicemail[]> {
    const snapshot = structuredClone(this.messages);
    await this.delay();
    if (this.scenario === 'load-error') {
      throw new Error('Calls could not be loaded. Please try again.');
    }
    return snapshot;
  }

  override async getSettings(): Promise<VmSettings> {
    const snapshot = structuredClone(this.settings);
    await this.delay();
    return snapshot;
  }

  /**
   * The demo has no carrier to ask, so a check finds what it already knows. It exists so the demo
   * satisfies the same boundary as the API gateway rather than the screen having two shapes.
   */
  override async reconcile(): Promise<VmSettings> {
    const snapshot = structuredClone(this.settings);
    await this.delay();
    return snapshot;
  }

  override async saveSettings(settings: VmSettings): Promise<VmSettings> {
    await this.delay();
    if (this.scenario === 'save-error') {
      throw new Error('Your changes could not be saved. Please try again.');
    }
    // Only the greeting and the override are stored with the line; the derived booking link is
    // re-read from the scheduling setup on every load and cannot be saved from here.
    this.settings = {
      ...this.settings,
      greeting: settings.greeting,
      overrideUrl: settings.overrideUrl.trim(),
    };
    return structuredClone(this.settings);
  }

  override async setWebsiteNumber(
    useVoicemailNumber: boolean
  ): Promise<VmSettings> {
    await this.delay();
    if (this.settings.state !== 'active') {
      throw new Error('Your voicemail line is not active.');
    }
    if (!useVoicemailNumber && !this.settings.practicePhone) {
      throw new Error(
        'Add a practice phone number in Account settings before showing it on your website instead.'
      );
    }
    this.settings = {
      ...this.settings,
      useVoicemailNumberOnWebsite: useVoicemailNumber,
    };
    return structuredClone(this.settings);
  }

  override async activate(): Promise<VmSettings> {
    await this.delay();
    if (
      this.settings.state === 'checking' ||
      this.settings.state === 'support-required'
    ) {
      throw new Error(
        'Activation must be resolved before another number can be requested.'
      );
    }
    if (!this.settings.overrideUrl && !this.settings.bookingLink.url) {
      throw new Error(
        'Publish your landing page or add a different link for texts before enabling voicemail.'
      );
    }
    if (this.scenario === 'provisioning-failed') {
      this.settings = { ...this.settings, state: 'failed', phoneNumber: null };
    } else {
      this.settings = {
        ...this.settings,
        state: 'active',
        phoneNumber: '(503) 555-0199',
      };
    }
    return structuredClone(this.settings);
  }

  override async markUnread(id: string, unread: boolean): Promise<Voicemail> {
    await this.delay();
    return this.update(id, item => ({ ...item, unread }));
  }

  override async searchPatients(query: string): Promise<VmPatient[]> {
    await this.delay();
    if (this.scenario === 'search-error') {
      throw new Error('Patients could not be searched. Please try again.');
    }
    const text = query.trim().toLowerCase();
    const digits = text.replace(/\D/g, '');
    if (!text) return [];
    return VM_DEMO_PATIENTS.filter(
      patient =>
        patient.name.toLowerCase().includes(text) ||
        (digits.length > 0 && patient.phone.replace(/\D/g, '').includes(digits))
    ).map(patient => ({ ...patient }));
  }

  override async linkPatient(
    id: string,
    patientId: string | null
  ): Promise<Voicemail> {
    await this.delay();
    if (this.scenario === 'link-error') {
      throw new Error(
        'The patient could not be linked. Your selection has been kept.'
      );
    }
    const patient =
      VM_DEMO_PATIENTS.find(item => item.id === patientId) ?? null;
    if (patientId && !patient)
      throw new Error('This patient is no longer available. Search again.');
    return this.update(id, item => ({
      ...item,
      patient: patient ? { ...patient } : null,
      matchType: patient ? 'manual' : 'unknown',
      // Linking changes identity only, preserving the original SMS outcome.
      sms: item.sms,
    }));
  }

  private skipSms(id: string, reason: VmSmsReason): void {
    const item = this.messages.find(message => message.id === id);
    if (!item) return;
    item.sms = {
      status: 'not-sent',
      reason,
      attemptedAt: null,
      sentAt: null,
      destination: item.callerNumber,
      message: null,
    };
  }

  private update(
    id: string,
    mutate: (item: Voicemail) => Voicemail
  ): Voicemail {
    const index = this.messages.findIndex(item => item.id === id);
    if (index < 0)
      throw new Error(
        'This voicemail is no longer available. Reload your calls.'
      );
    this.messages[index] = mutate(this.messages[index]);
    return structuredClone(this.messages[index]);
  }

  private async delay(): Promise<void> {
    const revision = this.revision;
    await new Promise<void>(resolve => setTimeout(resolve, 350));
    if (revision !== this.revision)
      throw new Error('The demo scenario changed. Reload the current view.');
  }
}
